import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

/* Mesh WebRTC over polling signaling (/api/rtc). Lower peer_id makes the offer. */
export function useMeeting(confId, joinBody, enabled) {
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [local, setLocal] = useState(null);
  const [peers, setPeers] = useState({});
  const [chat, setChat] = useState([]);
  const [ended, setEnded] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sharingId, setSharingId] = useState(null);
  const [hands, setHands] = useState({});
  const [promo, setPromo] = useState(null);
  const [rawCam, setRawCam] = useState(null);
  const [logoOn, setLogoOn] = useState(true);
  const viewer = !!(joinBody?.view_code && !joinBody?.promo);
  const pcs = useRef({});
  const pendingIce = useRef({});
  const camTrack = useRef(null);
  const screenTrack = useRef(null);
  const meRef = useRef(null);
  const localRef = useRef(null);
  const rawCamRef = useRef(null);
  const seen = useRef(new Set());

  const send = useCallback((to, type, data) => meRef.current && api.post(`/api/rtc/${confId}/signal`, { token: meRef.current.token, to, type, data }).catch(() => {}), [confId]);
  const patchPeer = (id, v) => setPeers(p => ({ ...p, [id]: { ...(p[id] || {}), ...v } }));

  const makePc = useCallback((id, name, offerer = false) => {
    if (pcs.current[id]) return pcs.current[id];
    const pc = new RTCPeerConnection({ iceServers: meRef.current.ice_servers });
    pc._at = Date.now(); pc._offerer = offerer;
    const stream = localRef.current;
    stream?.getAudioTracks().forEach(t => pc.addTrack(t, stream));
    const v = screenTrack.current || camTrack.current;
    if (v) pc.addTrack(v, stream || new MediaStream());
    else if (offerer) pc.addTransceiver("video", { direction: "sendrecv" });
    pc.onicecandidate = (e) => e.candidate && send(id, "ice", e.candidate.toJSON());
    const remote = new MediaStream();
    pc.ontrack = (e) => {
      remote.getTracks().filter(t => t.kind === e.track.kind && t !== e.track).forEach(t => remote.removeTrack(t));
      if (!remote.getTracks().includes(e.track)) remote.addTrack(e.track);
      const bump = () => patchPeer(id, { stream: remote, v: Date.now() });
      e.track.onunmute = bump; e.track.onmute = bump; bump();
    };
    pc.onconnectionstatechange = () => {
      if (pcs.current[id] !== pc) return;
      const st = pc.connectionState;
      if (st === "connected") { pc._ok = true; pc._bad = 0; } else if (["failed", "disconnected"].includes(st)) pc._bad = pc._bad || Date.now();
      patchPeer(id, { state: st });
      if (pc.connectionState === "connected" && screenTrack.current) send(id, "screen", { on: true });
      if (pc.connectionState === "connected" && lastCams.current) tune(lastCams.current);
    };
    pcs.current[id] = pc;
    patchPeer(id, { name, state: "connecting" });
    return pc;
  }, [send]);

  const drop = (id) => { setHands(h => { const n = { ...h }; delete n[id]; return n; }); pcs.current[id]?.close(); delete pcs.current[id]; setSharingId(x => (x === id ? null : x)); setPeers(p => { const n = { ...p }; delete n[id]; return n; }); };

  const handle = useCallback(async (s) => {
    const id = s.from;
    if (s.type === "chat") return setChat(c => [...c, s.data]);
    if (s.type === "end") return setEnded(true);
    if (s.type === "record") return setRecording(!!s.data?.on);
    if (s.type === "screen") return setSharingId(x => (s.data?.on ? id : x === id ? null : x));
    if (s.type === "bye") return drop(id);
    if (s.type === "hand") return setHands(h => { const n = { ...h }; if (s.data?.up) n[id] = s.from_name; else delete n[id]; return n; });
    if (s.type === "promote") return s.data?.promo && setPromo(s.data.promo);
    if (s.type === "offer" && pcs.current[id]?.remoteDescription) { pcs.current[id].close(); delete pcs.current[id]; pendingIce.current[id] = []; }
    const pc = makePc(id, s.from_name);
    try {
      if (s.type === "offer") {
        await pc.setRemoteDescription(s.data);
        const vt = pc.getTransceivers().find(t => t.mid != null && t.receiver.track?.kind === "video");
        if (vt && !vt.sender.track) { vt.direction = "sendrecv"; const cur = screenTrack.current || camTrack.current; if (cur) await vt.sender.replaceTrack(cur); }
        await pc.setLocalDescription(await pc.createAnswer());
        send(id, "answer", pc.localDescription.toJSON());
      } else if (s.type === "answer") {
        if (pc.signalingState === "have-local-offer") await pc.setRemoteDescription(s.data);
      } else if (s.type === "ice") {
        if (pc.remoteDescription) await pc.addIceCandidate(s.data);
        else (pendingIce.current[id] = pendingIce.current[id] || []).push(s.data);
        return;
      }
      for (const c of pendingIce.current[id] || []) await pc.addIceCandidate(c).catch(() => {});
      pendingIce.current[id] = [];
    } catch (e) { console.warn("signal", s.type, e); }
  }, [makePc, send]);

  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false, since = 0, timer;
    (async () => {
      let stream = null;
      if (viewer) stream = new MediaStream();
      else try { stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: window.matchMedia?.("(pointer: coarse)").matches ? { facingMode: "user" } : { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } } }); }
      catch { try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { stream = new MediaStream(); } }
      if (stop) { stream.getTracks().forEach(t => t.stop()); return; }
      camTrack.current = stream.getVideoTracks()[0] || null;
      localRef.current = stream;
      setLocal(stream);
      setRawCam(camTrack.current);
      rawCamRef.current = camTrack.current;
      try {
        const { data } = await api.post(`/api/rtc/${confId}/join`, joinBody);
        if (stop) { api.post(`/api/rtc/${confId}/leave`, { token: data.token }).catch(() => {}); return; }
        meRef.current = data; setMe(data); since = data.since || 0;
      } catch (e) { if (!stop) setError(e.response?.data?.detail || "Couldn't join the meeting"); return; }
      const tick = async () => {
        if (stop) return;
        try {
          const { data } = await api.get(`/api/rtc/${confId}/poll`, { params: { token: meRef.current.token, since } });
          for (const s of data.signals) {
            since = Math.max(since, s.seq);
            const key = s.sid || `${s.from}:${s.seq}`;
            if (seen.current.has(key)) continue;
            seen.current.add(key);
            await handle(s);
          }
          if (seen.current.size > 3000) seen.current = new Set([...seen.current].slice(-1500));
          const live = new Set(data.peers.map(p => p.peer_id));
          for (const p of data.peers) {
            const meV = meRef.current.role === "viewer", themV = p.role === "viewer";
            const offer = !meV && (themV || meRef.current.peer_id < p.peer_id);
            const old = pcs.current[p.peer_id];
            const retry = old && old._offerer && ((old._bad && Date.now() - old._bad > 5000) || (!old._ok && Date.now() - old._at > 10000));
            if (retry) { old.close(); delete pcs.current[p.peer_id]; pendingIce.current[p.peer_id] = []; }
            if (!pcs.current[p.peer_id] && offer && !(meV && themV)) {
              const pc = makePc(p.peer_id, p.name, true);
              patchPeer(p.peer_id, { role: p.role || "camera", is_host: p.is_host, ...(retry ? { state: "reconnecting" } : {}) });
              await pc.setLocalDescription(await pc.createOffer());
              send(p.peer_id, "offer", pc.localDescription.toJSON());
            } else if (pcs.current[p.peer_id]) patchPeer(p.peer_id, { name: p.name, is_host: p.is_host, role: p.role || "camera" });
          }
          Object.keys(pcs.current).forEach(id => !live.has(id) && drop(id));
          const cams = 1 + data.peers.filter(p => p.role !== "viewer").length;
          if (cams !== lastCams.current) { lastCams.current = cams; tune(cams); }
          if (data.status === "ended") setEnded(true);
          setLogoOn(data.logo_on !== false);
        } catch (e) { if (e.response?.status === 401) setEnded(true); }
        timer = setTimeout(tick, 1000);
      };
      tick();
    })();
    const onHide = () => meRef.current && fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rtc/${confId}/leave`, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: meRef.current.token }) });
    window.addEventListener("pagehide", onHide);
    return () => {
      stop = true; clearTimeout(timer); window.removeEventListener("pagehide", onHide);
      if (!meRef.current) { localRef.current?.getTracks().forEach(t => t.stop()); return; }
      if (meRef.current) { send("*", "bye"); api.post(`/api/rtc/${confId}/leave`, { token: meRef.current.token }).catch(() => {}); }
      Object.keys(pcs.current).forEach(drop);
      localRef.current?.getTracks().forEach(t => t.stop());
      rawCamRef.current?.stop();
      screenTrack.current?.stop();
    };
  }, [enabled, confId]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastCams = useRef(0);
  const tune = (cams) => Object.values(pcs.current).forEach(pc => pc.getSenders().forEach(snd => {
    if (snd.track?.kind !== "video" || snd.track === screenTrack.current) return;
    const p = snd.getParameters(); if (!p.encodings?.length) p.encodings = [{}];
    p.encodings[0].maxBitrate = cams > 4 ? 350000 : cams > 2 ? 800000 : 1500000;
    p.encodings[0].scaleResolutionDownBy = cams > 4 ? 2 : 1;
    snd.setParameters(p).catch(() => {});
  }));
  const swapVideo = (track) => Object.values(pcs.current).forEach(pc => {
    const all = pc.getTransceivers().filter(t => t.receiver.track?.kind === "video" || t.sender.track?.kind === "video");
    const tr = all.find(t => t.mid != null) || all[0];
    if (!tr) return;
    tr.sender.replaceTrack(track).then(() => {
      if (track && track === screenTrack.current) {
        const p = tr.sender.getParameters(); if (!p.encodings?.length) p.encodings = [{}];
        p.encodings[0].maxBitrate = 2500000; p.encodings[0].scaleResolutionDownBy = 1;
        tr.sender.setParameters(p).catch(() => {});
      } else if (lastCams.current) tune(lastCams.current);
    }).catch(e => console.warn("replaceTrack", e));
  });

  const stopShare = useCallback(() => {
    screenTrack.current?.stop(); screenTrack.current = null;
    swapVideo(camTrack.current);
    setSharingId(x => (x === "me" ? null : x)); send("*", "screen", { on: false });
  }, [send]);

  const startShare = useCallback(async () => {
    const s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15, max: 30 } }, audio: false });
    const t = s.getVideoTracks()[0];
    if ("contentHint" in t) t.contentHint = "detail";
    screenTrack.current?.stop();
    screenTrack.current = t;
    swapVideo(t);
    t.onended = stopShare;
    setSharingId("me"); send("*", "screen", { on: true });
  }, [send, stopShare]);

  const raiseHand = (up) => send("*", "hand", { up });
  const applyVideo = useCallback((track) => {
    camTrack.current = track;
    if (!screenTrack.current) swapVideo(track);
    const s = localRef.current;
    if (s) setLocal(new MediaStream([...s.getAudioTracks(), ...(track ? [track] : [])]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setLogo = (on) => api.post(`/api/rtc/${confId}/logo`, { token: meRef.current.token, on }).then(() => setLogoOn(on));
  const facing = useRef("user");
  const flipCamera = useCallback(async () => {
    const next = facing.current === "user" ? "environment" : "user";
    const old = rawCamRef.current;
    const get = async (f) => { try { return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: f } } }); } catch { return navigator.mediaDevices.getUserMedia({ video: { facingMode: f } }); } };
    let s;
    try { s = await get(next); }
    catch {
      old?.stop();
      try { s = await get(next); }
      catch (e) { const back = await get(facing.current).catch(() => null); const bt = back?.getVideoTracks()[0]; if (bt) { rawCamRef.current = bt; setRawCam(bt); if (!fxOn.current) applyVideo(bt); } throw e; }
    }
    const t = s.getVideoTracks()[0];
    facing.current = next;
    rawCamRef.current = t;
    setRawCam(t);
    if (!fxOn.current) applyVideo(t);
    old?.stop();
    return next;
  }, [applyVideo]); // eslint-disable-line react-hooks/exhaustive-deps
  const fxOn = useRef(false);
  const promote = (id) => api.post(`/api/rtc/${confId}/signal`, { token: meRef.current.token, to: id, type: "promote" }).then(() => setHands(h => { const n = { ...h }; delete n[id]; return n; }));
  return { me, error, local, peers, chat, setChat, ended, recording, setRecording, sharingId, send, startShare, stopShare, screenTrack, hands, promo, raiseHand, promote, viewer, rawCam, applyVideo, logoOn, setLogo, flipCamera, fxOn };
}
